import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewsIcon,
  TouchApp as MouseIcon,
  AttachMoney as RevenueIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

const MarketingAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [metric, setMetric] = useState('all');

  const analyticsData = {
    overview: [
      {
        title: 'Total Impressions',
        value: '2.4M',
        change: '+15.2%',
        trend: 'up',
        icon: <ViewsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      },
      {
        title: 'Total Clicks',
        value: '68.5K',
        change: '+22.1%',
        trend: 'up',
        icon: <MouseIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      },
      {
        title: 'Conversion Rate',
        value: '3.2%',
        change: '+0.8%',
        trend: 'up',
        icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      },
      {
        title: 'Revenue Generated',
        value: '$184K',
        change: '+28.5%',
        trend: 'up',
        icon: <RevenueIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      },
    ],
    channels: [
      { name: 'Email Marketing', visitors: 15420, conversions: 892, revenue: 45600, ctr: 5.8 },
      { name: 'Social Media', visitors: 28930, conversions: 1456, revenue: 72800, ctr: 3.2 },
      { name: 'Google Ads', visitors: 12580, conversions: 756, revenue: 38900, ctr: 6.0 },
      { name: 'Display Ads', visitors: 8750, conversions: 287, revenue: 18400, ctr: 3.3 },
      { name: 'Referrals', visitors: 5240, conversions: 314, revenue: 15800, ctr: 6.0 },
    ],
    demographics: {
      ageGroups: [
        { range: '18-24', percentage: 12, count: 8240 },
        { range: '25-34', percentage: 28, count: 19180 },
        { range: '35-44', percentage: 35, count: 23980 },
        { range: '45-54', percentage: 18, count: 12330 },
        { range: '55+', percentage: 7, count: 4790 },
      ],
      locations: [
        { city: 'New York', percentage: 22, count: 15070 },
        { city: 'Los Angeles', percentage: 18, count: 12330 },
        { city: 'Chicago', percentage: 15, count: 10280 },
        { city: 'Houston', percentage: 12, count: 8220 },
        { city: 'Other', percentage: 33, count: 22620 },
      ],
    },
    topProducts: [
      { name: 'Modern Sofa Set', views: 12400, sales: 156, revenue: 38400 },
      { name: 'Oak Dining Table', views: 9800, sales: 89, revenue: 16800 },
      { name: 'Queen Bed Frame', views: 8600, sales: 124, revenue: 11000 },
      { name: 'Glass Coffee Table', views: 7200, sales: 98, revenue: 3100 },
      { name: 'Leather Recliner', views: 6800, sales: 67, revenue: 8400 },
    ],
  };

  const getConversionRate = (conversions, visitors) => {
    return ((conversions / visitors) * 100).toFixed(1);
  };

  const getRevenuePerVisitor = (revenue, visitors) => {
    return (revenue / visitors).toFixed(2);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Marketing Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7days">Last 7 days</MenuItem>
              <MenuItem value="30days">Last 30 days</MenuItem>
              <MenuItem value="90days">Last 90 days</MenuItem>
              <MenuItem value="1year">Last year</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<AssessmentIcon />}>
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {analyticsData.overview.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {stat.trend === 'up' ? (
                      <TrendingUpIcon color="success" fontSize="small" />
                    ) : (
                      <TrendingDownIcon color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: stat.trend === 'up' ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                        ml: 0.5,
                      }}
                    >
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Channel Performance */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Channel Performance
          </Typography>
          <Grid container spacing={2}>
            {analyticsData.channels.map((channel, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {channel.name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Visitors</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {channel.visitors.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Conversions</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {channel.conversions}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Revenue</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        ${channel.revenue.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Conv. Rate</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {getConversionRate(channel.conversions, channel.visitors)}%
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={`CTR: ${channel.ctr}%`} 
                      color={channel.ctr > 5 ? 'success' : channel.ctr > 3 ? 'warning' : 'error'} 
                      size="small" 
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Demographics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Audience Demographics
              </Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Age Groups
              </Typography>
              {analyticsData.demographics.ageGroups.map((group, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{group.range}</Typography>
                    <Typography variant="body2">{group.percentage}% ({group.count.toLocaleString()})</Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 8,
                      backgroundColor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: 'primary.main',
                        width: `${group.percentage}%`,
                      }}
                    />
                  </Box>
                </Box>
              ))}

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 3 }}>
                Top Locations
              </Typography>
              {analyticsData.demographics.locations.map((location, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{location.city}</Typography>
                    <Typography variant="body2">{location.percentage}% ({location.count.toLocaleString()})</Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 8,
                      backgroundColor: 'grey.200',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: 'info.main',
                        width: `${location.percentage}%`,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                Top Performing Products
              </Typography>
              {analyticsData.topProducts.map((product, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {product.name}
                    </Typography>
                    <Chip label={`#${index + 1}`} color="primary" size="small" />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Views</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {product.views.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Sales</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {product.sales}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Revenue</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        ${product.revenue.toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Conversion Rate: {getConversionRate(product.sales, product.views)}%
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Placeholder */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Performance Trends
          </Typography>
          <Box
            sx={{
              height: 300,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <TimelineIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Interactive Charts Coming Soon
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detailed performance trends and comparative analytics will be displayed here
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MarketingAnalytics;
